import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { SoundKey } from '../../services/soundService';

type BtnKind = 'default' | 'primary' | 'dark' | 'ghost';
type BtnSize = '' | 'lg' | 'xl';

interface BtnProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  kind?: BtnKind;
  size?: BtnSize;
  full?: boolean;
  icon?: keyof typeof LucideIcons;
  iconRight?: keyof typeof LucideIcons;
  loading?: boolean;
  /**
   * Override the press sound. Defaults to `tap` on primary/dark buttons and
   * nothing at all elsewhere.
   *
   * The one button that needs this is "I'M DONE": locking in your answers is a
   * paper flip, not a tap, and a button that played BOTH would be the same
   * click twice. `'none'` silences a loud button that shouldn't be.
   */
  sound?: SoundKey | 'none';
  children: React.ReactNode;
}

/**
 * forwardRef because the desktop race needs to move FOCUS to this button:
 * once all four fields are full, Enter arms the Done button by focusing it,
 * and the second Enter is the browser's own button activation. That whole
 * two-step — which exists so that nobody accidentally locks in a half-round
 * during a squeeze — needs a handle on the DOM node.
 */
export const Btn = React.forwardRef<HTMLButtonElement, BtnProps>(function Btn({
  kind = 'default',
  size = '',
  full,
  icon,
  iconRight,
  loading,
  disabled,
  sound,
  children,
  className = '',
  ...props
}: BtnProps, ref) {
  const Icon = icon ? ((LucideIcons[icon] || null) as LucideIcon | null) : null;
  const IconRight = iconRight ? ((LucideIcons[iconRight] || null) as LucideIcon | null) : null;
  const iconSize = size === 'xl' ? 22 : size === 'lg' ? 20 : 18;

  const classes = [
    'btn',
    kind !== 'default' ? kind : '',
    size,
    full ? 'full' : '',
    disabled || loading ? 'disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /**
   * Every primary/dark CTA plays a 30ms felt `tap`. Ghost and default buttons
   * stay silent — they are the cancel/back/secondary paths, and a room where
   * every control chirps is a room nobody leaves the sound on in.
   *
   * A click is also the gesture that UNLOCKS audio: browsers refuse to play
   * anything before one, so the first button press in a session is what wakes
   * the AudioContext and lazily fetches the files. That is why `unlock()` runs
   * even for a silent button.
   */
  const key: SoundKey | 'none' =
    sound ?? ((kind === 'primary' || kind === 'dark') ? 'tap' : 'none');

  const originalOnClick = props.onClick;
  const wrappedOnClick = originalOnClick
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !loading) {
          // Lazy import: the auth screens should not pull the audio service
          // (or its 169KB of buffers) until someone actually presses something.
          import('../../services/soundService').then(m => {
            try {
              void m.soundService.unlock();
              if (key !== 'none') m.soundService.play(key);
            } catch {}
          }).catch(() => {});
        }
        originalOnClick(e);
      }
    : originalOnClick;

  return (
    <button ref={ref} {...props} onClick={wrappedOnClick} disabled={disabled || loading} className={classes}>
      {loading ? (
        <LucideIcons.Loader2 size={iconSize} className="wr-spin" />
      ) : (
        Icon && <Icon size={iconSize} strokeWidth={2.4} />
      )}
      <span>{children}</span>
      {IconRight && <IconRight size={iconSize} strokeWidth={2.4} />}
    </button>
  );
});

/* Inline keyframe for the loading spinner. Tiny so it lives here. */
const styleId = 'wr-btn-spin-style';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const s = document.createElement('style');
  s.id = styleId;
  s.textContent = `@keyframes wr-spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} } .wr-spin { animation: wr-spin 1s linear infinite; }`;
  document.head.appendChild(s);
}
